import {Injectable} from '@angular/core';
import {MatDialog, MatDialogConfig, MatDialogRef} from '@angular/material/dialog';
import moment from 'moment';
import {BehaviorSubject, Observable} from "rxjs";
import {OpenDialog} from "../common/interfaces/global-interfaces";

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  trigger = 0;
  triggerTimer;

  show_help = new BehaviorSubject(false);

  private dialogRefs = new Map<any, MatDialogRef<any>>();

  constructor(
    public dialog: MatDialog,
  ) {
  }

  closeDialog<T>(component: T): void {
    const dialogRef = this.dialogRefs.get(component);
    if (dialogRef) {
      dialogRef.close();
      this.dialogRefs.delete(component);
    }
  }

  storeDialogRef<T>(component: T, dialogRef: MatDialogRef<T>): void {
    this.dialogRefs.set(component, dialogRef);
  }

  showHelp() {
    this.show_help.next(true);
  }

  closeHelp() {
    this.show_help.next(false);
  }

  removeHtmlTags(htmlString: string): string {
    if (!htmlString) return;
    return htmlString.replace(/<[^>]*>/g, '');
  }

  formatDateForDateTimeLocal(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  formatDateWithForwardSlash(date: Date) {

    return this.formatDate(date, 'dd/MM/yyyy');
  }

  decendingDateformatDate(date: string | number | Date, format: string = 'yyyy-MM-dd HH:mm'): string {
    return this.formatDate(date, format);
  }

  getTableObjectValue(object: any, keyword: string) {
    const keyArray = keyword?.split('.');
    let value = object;

    if (keyArray?.length > 0) {
      keyArray?.forEach((key) => {
        if (value != null) {
          if (Object?.keys(value)?.findIndex(k => k === key) > -1) {
            value = value[key];
          } else {
            return null;
          }
        }
      });
    }
    return value;
  }
  formatDegrees(val: string){
    return val + '°C';
    // return unit === 'CENTIGRADE'? val+'°C':val+'°F'
  }
  getWeekNumber(d: Date): number {
    const oneJan = new Date(d.getFullYear(), 0, 1);
    const numberOfDays = Math.floor(
        (d.getTime() - oneJan.getTime()) / (24 * 60 * 60 * 1000)
    );
    return Math.ceil((numberOfDays + oneJan.getDay() + 1) / 7);
  }
  formatDate(dateReceived, format: string = 'YYYY-MM-DD') {
    if (typeof dateReceived === 'string' && dateReceived !== '') {
      dateReceived = new Date(dateReceived);
    }
    return dateReceived ? moment(dateReceived).format(format) : undefined;
  }

  addDays = (date: Date, days: number): Date => {
    let result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };


  reformatDate(dateReceived, format: string = 'MM/DD/YYYY') {
    if (typeof dateReceived === 'string' && dateReceived !== '') {
      dateReceived = new Date(dateReceived);
    }
    return dateReceived ? moment(dateReceived).format(format) : undefined;
  }

  formatDateStartWithYearAndDash(date: string | number | Date, format: string = 'YYYY-MM-DD'): string {
    return this.formatDate(date, format);
  }


  formatDateToLocalDate(date: Date) {
    if (!date) return null;
    let startDate = this.formatDateWithForwardSlash(date);
    const splittedStartDate = startDate?.replace(/\//ig, '-')?.split('-');
    return !!splittedStartDate ? splittedStartDate[2] + '-' + splittedStartDate[1] + '-' + splittedStartDate[0] : '';
  }

  enumToArray(inputEnum: any) {
    return Object.keys(inputEnum).map(key => ({
      value: key,
      name: inputEnum[key as keyof typeof inputEnum]
    }))
  }



  insertIntoArrIndex(arr: any[], index: number, item: any): any[] {
    arr.splice(index, 0, item);
    return arr;
  }

  b64toBlob(b64Data: string, contentType: string, sliceSize = 512) {
    contentType = contentType || '';
    sliceSize = sliceSize || 512;
    const byteCharacters = atob(b64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, {type: contentType});
    return URL.createObjectURL(blob);
  }

  encoder(data: string, loop = 2, key = 'secured'): string {
    let returnedData = btoa(data);
    const keyValue = btoa(key);
    returnedData = btoa(keyValue + returnedData);
    for (let i = 0; i < loop; i++) {
      returnedData = btoa(returnedData);
    }
    return returnedData;
  }

  decoder(data: any, loop = 2, key = 'secured') {
    let returnedData = data;

    for (let i = 0; i < loop; i++) {
      returnedData = atob(returnedData);
    }
    const keyValue = btoa(key);
    returnedData = atob(returnedData);

    return atob(returnedData.substr(keyValue.length));
  }

  openDialogModal(dialog: OpenDialog): Observable<any> {
    const dialogConfig = new MatDialogConfig();
    const classes: string[] = ['rounded-xl'];
    if (dialog?.panelClass && dialog?.panelClass?.length > 0) {
      classes.push(...dialog?.panelClass);
    }
    dialogConfig.panelClass = classes;
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = dialog?.width ? dialog?.width : '50%';
    dialogConfig.data = dialog?.data;
    if (dialog?.position != null) {
      dialogConfig.position = dialog?.position;
    }
    if (dialog?.height != null) {
      dialogConfig.height = dialog?.height;
    }
    const dialogRef = this.dialog.open(dialog?.component, dialogConfig);

    // store dialog to make sure it available when you need to close it dynamically
    this.storeDialogRef(dialog.component, dialogRef);
    return dialogRef.afterClosed();
  }

  generatePagination(current: number, lastPage: number) {
    // return  Array((totalPages === 1) ? 1 : totalPages).fill(0).map((x, i) => ({id: i}));
    const delta = 6;
    const range = [0];
    const rangeWithDots = [];
    let l;

    range.push(1);
    for (let i = current - delta; i <= current + delta; i++) {
      if (i < lastPage && i > 1) {
        range.push(i);
      }
    }
    range.push(lastPage);

    for (const i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    }
    return rangeWithDots;
  }

  formatDateMonthName(date: string | number | Date): string {
    return this.formatDate(date, 'mediumDate');
  }

  checkPdfStatus(filePath: string) {
    let isPdf: boolean;
    let fileExtension = filePath.split('.').pop();

    if (fileExtension?.startsWith('pdf')) {
      isPdf = true;

    } else {
      isPdf = false;
    }

    return isPdf
  }

  isNullOrUndefined(value: any) {
    return value === 'undefined' || value === 'null' || value === undefined || typeof value === 'undefined' || value === null || Number.isNaN(value)
      || (typeof value === 'object' && Object.keys(value).length === 0) || (typeof value === 'string' && value.trim().length === 0);

  }

  isArray = (val: any) => {
    if (val === null) {
      return false;
    }
    return Object.prototype.toString.call(val) === '[object Array]';
  }


  sortArray(array: any[], property: any, order: 'ASC' | 'DESC'): any[] {
    try {
      if (array === null || property === null) {
        return array;
      }
      const pos = order === 'ASC' ? 1 : -1;
      const neg = order === 'ASC' ? -1 : 1;
      return array.slice().sort((firstEl, secondEl) => {
        if (firstEl[property] < secondEl[property]) {
          return neg;
        }
        if (firstEl[property] > secondEl[property]) {
          return pos;
        }
        return 0;
      });
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  multipleClickEvent(callback?: (x?: any) => void | any, input?: {
    threshold?: number,
    callBackInput?: any
  } | number | any, threshold?) {
    if (threshold && !input?.callBackInput) input = {threshold: threshold, callBackInput: input};
    else if (typeof input === 'number') input = {threshold: input};

    if (this.triggerTimer) clearTimeout(this.triggerTimer);
    this.triggerTimer = setTimeout(() => {
      this.trigger = 0;
    }, 500);
    if ((++this.trigger < (input?.threshold ?? 3))) return false;
    callback(input?.callBackInput);
    this.trigger = 0;
    return this.trigger;
  }


  isObject = (val) => {
    if (val === null) {
      return false;
    }
    return ((typeof val === 'function') || (typeof val === 'object'));
  }

  toTitleCase(str?: string) {
    if (!str) {
      return str;
    }
    return str.replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  }

  removeDuplicates(array: any[], property: any = null): any[] {
    if (array === null) {
      return [];
    }
    return array.filter((item, index, self) => {
      if (property != null) {
        const clone = [...array];
        if (!clone[0].hasOwnProperty(property)) {
          throw new Error(`Could not find property [${property}] in Object Array`);
        }
        return index === self.findIndex((t) => (
          t[property] === item[property]
        ));
      } else {
        const _item = JSON.stringify(item);
        return index === array.findIndex(obj => {
          return JSON.stringify(obj) === _item;
        });
      }
    });
  }

  makeId(): string {
    let text = '';
    const first_letter_possible_combinations =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    const possible_combinations =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    for (let i = 0; i < 18; i++) {
      if (i === 0) {
        text += first_letter_possible_combinations.charAt(
          Math.floor(Math.random() * possible_combinations.length)
        );
      } else {
        text += possible_combinations.charAt(
          Math.floor(Math.random() * possible_combinations.length)
        );
      }
    }
    return text;
  }


  generateYears(): Year[] {
    let years: Year[] = [];
    let currentYear: number = new Date().getFullYear();
    let yearsBackCount = 10;
    for (let i = 0; i < yearsBackCount; i++) {
      years.push({
        name: (currentYear - i).toString(),
        value: (currentYear - i)
      });
    }
    return years;
  }


  getBooleanFromString(value: string): boolean {
    if (value.toLowerCase() === "true") {
      return true;
    } else if (value.toLowerCase() === "false") {
      return false;
    } else {
      throw new Error("Invalid boolean string");
    }
  }

  sanitizeInput(text) {
    'use strict';
    return text.replace(/[\"&'\/<>]/g, function (a) {
      return {
        '"': '&quot;', '&': '&amp;', "'": '&#39;',
        '/': '&#47;', '<': '&lt;', '>': '&gt;'
      }[a];
    });
  }

  // checkExistenceInArray(arrayData: any[], key:string, value: string) {
  //   arrayData.
  // }
}


export interface Year {
  name: string,
  value: number
}
